import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Tag } from 'tags/models/tags';
import { CustomFieldProp } from 'admin/customFields/entities/customFields.entity';
import { simProductQueries } from '../queries/simProductsQueries';
import { SimData } from 'src/common/components/Sim/constants/sim';

export const useRefreshDetailEntry = (iccid: string) => {
  const queryClient = useQueryClient();

  const updateSim = useCallback(
    async (entryToUpdate: SimData) => {
      const queryKey = simProductQueries.detail({ iccid });
      await queryClient.cancelQueries({ queryKey: queryKey });

      const simProduct: SimData | undefined = queryClient.getQueryData(queryKey);

      if (!simProduct) {
        return;
      }

      const updatedSimProfile = {
        ...simProduct,
        ...entryToUpdate,
      };

      queryClient.setQueryData(queryKey, updatedSimProfile);
    },
    [iccid],
  );

  const refreshTags = useCallback(
    async ({ tags }: { tags: Tag[] }) => {
      const queryKey = simProductQueries.detail({ iccid });
      await queryClient.cancelQueries({ queryKey });

      const currentCardDetails: any = queryClient.getQueryData(queryKey);

      const updatedCardDetails = {
        ...currentCardDetails,
        tags: {
          ...currentCardDetails.tags,
          items: tags,
        },
      };

      queryClient.setQueryData(queryKey, updatedCardDetails);
    },
    [queryClient, iccid],
  );

  const refreshCustomFields = useCallback(
    async ({ customFieldsUpdate }: { customFieldsUpdate: Record<string, CustomFieldProp> }) => {
      const queryKey = simProductQueries.detail({ iccid });
      await queryClient.cancelQueries({ queryKey: queryKey });

      const currentCardDetails: any = queryClient.getQueryData(queryKey);

      const updatedCardDetails = {
        ...currentCardDetails,
        customFields: Object.values(customFieldsUpdate),
        ...customFieldsUpdate,
      };

      queryClient.setQueryData(queryKey, updatedCardDetails);
    },
    [queryClient, iccid],
  );

  return {
    refreshTags,
    refreshCustomFields,
    updateSim,
  };
};
